import { useQuery } from "@tanstack/react-query";

import { api } from "@/lib/api-client";
import { useParams } from "react-router";
import { Landlord, PermissionsResponse } from "@/types/api";

type LandlordResponse = { data: Landlord };

// api/v1/landlords/:id
export const getLandlord = (landlordId: Landlord["id"] | undefined): Promise<{ data: LandlordResponse }> => {
  if (!landlordId) {
    return Promise.reject(new Error("Landlord ID is required"));
  }
  return api.get<LandlordResponse>(`/api/v1/landlords/${landlordId}`);
};

export const useLandlordApi = ({ landlordId }: { landlordId: Landlord["id"] | undefined }) => {
  const query = useQuery({
    queryKey: ["landlord", landlordId],
    queryFn: () => getLandlord(landlordId).then((response) => response.data),
    // Retry a couple of times so a transient failure on the first authenticated request
    // right after an OAuth full-page reload doesn't wrongly bounce the user to sign-in.
    retry: 2
  });

  return { data: query.data, isLoading: query.isLoading, isError: query.isError };
};

export const useCurrentLandlord = () => {
  const landlordId = useParams<{ landlordId: string }>().landlordId;
  return useLandlordApi({ landlordId });
};

// Default pricing features to check for permissions
// These determine which property-level feature flags are included in the response
const DEFAULT_PRICING_FEATURES = ["rentable_gbp", "gbp_reviews"];

export const usePermissionsApi = (
  landlordId: Landlord["id"],
  pricingFeatures: string[] = DEFAULT_PRICING_FEATURES,
  propertyIds?: string[]
) => {
  return useQuery<PermissionsResponse>({
    queryKey: ["permissions", landlordId, pricingFeatures, propertyIds],
    queryFn: () => getPermissions(landlordId, pricingFeatures, propertyIds).then((response) => response.data),
    enabled: !!landlordId,
    retry: false
  });
};

const getPermissions = (landlordId: Landlord["id"], pricingFeatures: string[], propertyIds: string[] | undefined) => {
  return api.get<PermissionsResponse>(`/api/v1/landlords/${landlordId}/permissions`, {
    params: {
      pricing_features: pricingFeatures,
      property_ids: propertyIds
    }
  });
};
